import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import { generateArticleSchema, generateBreadcrumbSchema } from '../utils/schema';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/blog/${slug}`);
        setPost(response.data);
        
        // Fetch related posts by category
        const relatedResponse = await axios.get(`${API_URL}/api/blog?category=${response.data.category}`);
        const filtered = (relatedResponse.data.posts || []).filter(p => p.slug !== slug).slice(0, 3);
        setRelatedPosts(filtered);
      } catch (error) {
        console.error('Error fetching blog post:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen">
        <section className="bg-gradient-to-r from-blue-700 to-green-600 text-white py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl font-bold mb-4">Post Not Found</h1>
            <Link to="/blog" className="text-white underline">← Back to Blog</Link>
          </div>
        </section>
      </div>
    );
  }

  const schemas = [
    generateArticleSchema(post),
    generateBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Blog', url: '/blog' },
      { name: post.title, url: `/blog/${post.slug}` }
    ])
  ];

  return (
    <div>
      <SEO 
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt}
        keywords={post.tags?.join(', ')}
        ogType="article"
        ogImage={post.featured_image}
        schema={schemas}
        article={{
          publishedDate: post.published_date,
          modifiedDate: post.published_date,
          author: post.author,
          category: post.category,
          tags: post.tags
        }}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-700 to-green-600 text-white py-12">
        <div className="container mx-auto px-4">
          <Link to="/blog" className="text-white/80 hover:text-white text-sm mb-4 inline-flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Back to Blog
          </Link>
          <div className="max-w-4xl">
            <span className="bg-white/20 text-white text-sm px-3 py-1 rounded-full">{post.category}</span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-4">{post.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
              <span>By {post.author}</span>
              <span>•</span>
              <span>{new Date(post.published_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              {post.views > 0 && (
                <>
                  <span>•</span>
                  <span>{post.views} views</span>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {post.featured_image && (
                <img 
                  src={post.featured_image} 
                  alt={post.title}
                  className="w-full h-64 md:h-96 object-cover rounded-lg mb-8"
                />
              )}
              
              <article 
                className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-headings:font-bold prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900 prose-a:text-green-600"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="mt-8 pt-8 border-t">
                  <h3 className="text-sm font-semibold text-gray-500 mb-3">TAGS</h3>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag, index) => (
                      <span key={index} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Share Buttons */}
              <div className="mt-8 pt-8 border-t">
                <h3 className="text-sm font-semibold text-gray-500 mb-3">SHARE THIS ARTICLE</h3>
                <div className="flex gap-3">
                  <a 
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm"
                  >
                    Facebook
                  </a>
                  <a 
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-sky-500 text-white px-4 py-2 rounded-lg hover:bg-sky-600 transition text-sm"
                  >
                    Twitter
                  </a>
                  <a 
                    href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(post.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition text-sm"
                  >
                    LinkedIn
                  </a>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* CTA Box */}
              <div className="bg-green-600 text-white p-6 rounded-lg mb-8 sticky top-24">
                <h3 className="text-xl font-bold mb-3">Need Pest Control?</h3>
                <p className="text-white/90 mb-4 text-sm">Our expert exterminators serve all NYC areas. Fast, effective, guaranteed results.</p>
                <a 
                  href="tel:6462099955" 
                  className="block bg-white text-green-600 text-center py-3 rounded-lg font-bold mb-3 hover:bg-gray-100 transition"
                  data-testid="sidebar-call-btn"
                >
                  Call (646) 209-9955
                </a>
                <Link 
                  to="/contact" 
                  className="block bg-blue-600 text-white text-center py-3 rounded-lg font-bold hover:bg-blue-700 transition"
                  data-testid="sidebar-quote-btn"
                >
                  Get Free Quote
                </Link>
              </div>

              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-bold mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    {relatedPosts.map(related => (
                      <Link 
                        key={related.id} 
                        to={`/blog/${related.slug}`}
                        className="block group"
                      >
                        <div className="flex gap-3">
                          <img 
                            src={related.featured_image || '/images/services/general.jpg'} 
                            alt={related.title}
                            className="w-20 h-16 object-cover rounded"
                          />
                          <div>
                            <h4 className="font-medium text-sm group-hover:text-green-600 transition line-clamp-2">{related.title}</h4>
                            <span className="text-xs text-gray-500">{new Date(related.published_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-blue-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to Solve Your Pest Problem?</h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">Don&apos;t let pests take over your home or business. Our professional exterminators are ready to help with fast, effective solutions.</p>
          <Link to="/contact" className="bg-green-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-green-700 transition inline-block">
            Schedule Free Inspection
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
